// Editing localized content (contracts/messaging.md "Localized content", learn.md) before it goes to the API: a
// draft holds every language as plain strings per field; `toLocalized` turns it into the API's map. English is the
// source; the app falls back to English field by field for anything a language leaves empty.
//
// Messaging content (announcements, banners) needs a title and a message in every language it sends, so a
// language with some text but not those is "incomplete" and isn't sent. Learn content (stories, slides, brain
// cards) takes partial languages: there `required` is empty.

import { type Content, type ContentField, LOCALES, type Locale, MEDIA_MAX_BYTES, MEDIA_MIMES, type MediaMime } from "../api/messaging";

export type TextEntry<F extends string> = Record<F, string>;
export type TextDraft<F extends string> = Record<Locale, TextEntry<F>>;
/** Localized text as the API takes it: per language, the fields it has. */
export type LooseLocalized<F extends string> = Partial<Record<Locale, Partial<Record<F, string>>>>;

/** Announcements and banners (contracts/messaging.md). */
export const MESSAGE_FIELDS = ["title", "body", "detail", "cta"] as const satisfies readonly ContentField[];
/** What every language entry of a messaging Content must have. */
export const MESSAGE_REQUIRED = ["title", "body"] as const satisfies readonly ContentField[];
export type DraftEntry = TextEntry<ContentField>;
export type ContentDraft = TextDraft<ContentField>;

export function blankDraft<F extends string>(fields: readonly F[]): TextDraft<F> {
  return Object.fromEntries(LOCALES.map((l) => [l, Object.fromEntries(fields.map((f) => [f, ""]))])) as TextDraft<F>;
}

export function draftFrom<F extends string>(content: LooseLocalized<F>, fields: readonly F[]): TextDraft<F> {
  const draft = blankDraft(fields);
  for (const l of LOCALES) {
    const e = content[l];
    if (e) draft[l] = Object.fromEntries(fields.map((f) => [f, e[f] ?? ""])) as TextEntry<F>;
  }
  return draft;
}

export const emptyDraft = (): ContentDraft => blankDraft(MESSAGE_FIELDS);
export const draftFromContent = (content: Content): ContentDraft => draftFrom(content as LooseLocalized<ContentField>, MESSAGE_FIELDS);

/**
 * empty: nothing written (falls back to English). incomplete: some text but not every `required` field (the API
 * refuses it). partial: without required fields, some of English's fields aren't translated (they fall back).
 */
export type LocaleState = "empty" | "partial" | "complete" | "incomplete";

export function localeState<F extends string>(
  entry: TextEntry<F>,
  fields: readonly F[],
  { required = MESSAGE_REQUIRED as readonly string[], en }: { required?: readonly string[]; en?: TextEntry<F> } = {},
): LocaleState {
  const filled = (f: F) => (entry[f] ?? "").trim() !== "";
  if (!fields.some(filled)) return "empty";
  if (required.length) return required.every((r) => filled(r as F)) ? "complete" : "incomplete";
  return en && fields.some((f) => (en[f] ?? "").trim() !== "" && !filled(f)) ? "partial" : "complete";
}

/**
 * The map to send: English plus every language with text, trimmed, without empty fields. With `required`
 * fields, a language missing one is left out and listed in `incomplete` (the API refuses those).
 */
export function toLocalized<F extends string>(draft: TextDraft<F>, fields: readonly F[], required: readonly string[] = []): { content: LooseLocalized<F>; incomplete: Locale[] } {
  const content: LooseLocalized<F> = {};
  const incomplete: Locale[] = [];
  for (const l of LOCALES) {
    const e = draft[l];
    const state = localeState(e, fields, { required });
    if (l !== "en" && state === "incomplete") incomplete.push(l);
    if (l !== "en" && (state === "empty" || state === "incomplete")) continue;
    const entry: Partial<Record<F, string>> = {};
    for (const f of fields) {
      const v = (e[f] ?? "").trim();
      if (v) entry[f] = v;
    }
    content[l] = entry;
  }
  return { content, incomplete };
}

/** Messaging Content: every sent language has a title and a message. */
export function toContent(draft: ContentDraft, fields: readonly ContentField[]): { content: Content; incomplete: Locale[] } {
  const { content, incomplete } = toLocalized(draft, fields, MESSAGE_REQUIRED);
  return { content: content as Content, incomplete };
}

/** One language as the app shows it: each field falls back to English when it isn't translated. */
export function resolveEntry<F extends string>(draft: TextDraft<F>, locale: Locale): TextEntry<F> & { fallback: F[] } {
  const en = draft.en;
  const l = draft[locale];
  const fallback: F[] = [];
  const out = {} as TextEntry<F>;
  for (const f of Object.keys(en) as F[]) {
    const v = (l[f] ?? "").trim();
    if (v || locale === "en") out[f] = v;
    else {
      if (en[f].trim()) fallback.push(f);
      out[f] = en[f].trim();
    }
  }
  return { ...out, fallback };
}

/** A fingerprint of the English text, to tell when translations were made from an older version. */
export const englishKey = <F extends string>(draft: TextDraft<F>, fields: readonly F[]) => JSON.stringify(fields.map((f) => (draft.en[f] ?? "").trim()));

/** Merges a translation result into the draft; English stays as typed. A field the model left out stays empty (English). */
export function mergeTranslation<F extends string>(draft: TextDraft<F>, translated: Partial<Record<Locale, Record<string, string>>>): TextDraft<F> {
  const next = { ...draft };
  const fields = Object.keys(draft.en) as F[];
  for (const l of LOCALES) {
    const e = translated[l];
    if (l === "en" || !e) continue;
    next[l] = Object.fromEntries(fields.map((f) => [f, e[f] ?? ""])) as TextEntry<F>;
  }
  return next;
}

// ---------------------------------------------------------------- actions and links

/** https only, as the API checks (`url` actions open in Safari). */
export function isHttpsUrl(value: string): boolean {
  const s = value.trim();
  if (!s || s.length > 500) return false;
  try {
    const u = new URL(s);
    return u.protocol === "https:" && u.hostname.includes(".");
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- images

export const isMediaMime = (mime: string): mime is MediaMime => (MEDIA_MIMES as readonly string[]).includes(mime);

/** A friendly reason a file can't be uploaded, or null when it can. */
export function imageFileProblem(file: { type: string; size: number }): string | null {
  if (!isMediaMime(file.type)) return "Use a PNG, JPEG or WebP image.";
  if (file.size > MEDIA_MAX_BYTES) {
    return `The image is ${Math.round(file.size / 1000).toLocaleString("en-US")} KB; the limit is ${MEDIA_MAX_BYTES / 1000} KB. Export it smaller (1200 px wide is plenty).`;
  }
  return null;
}

/** The file's bytes as base64 (no data: prefix), as POST /media takes them. */
export function readFileBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Couldn't read the file."));
    reader.onload = () => {
      const s = String(reader.result ?? "");
      resolve(s.slice(s.indexOf(",") + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** A note when an image's shape is far from the recommended one (more than 10% off the aspect ratio). */
export function aspectNote(width: number, height: number, want: { width: number; height: number }): string | null {
  const ratio = width / height;
  const target = want.width / want.height;
  if (Math.abs(ratio - target) / target > 0.1) {
    return `This image is ${width}×${height}; ${want.width}×${want.height} is recommended, so the app will crop or letterbox it.`;
  }
  if (width < want.width * 0.5) return `This image is ${width}×${height}: small for ${want.width}×${want.height}, it may look soft.`;
  return null;
}

// ---------------------------------------------------------------- dates

/** An ISO time → the value of an <input type="datetime-local">, in UTC like every time in the admin. */
export function toUtcInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 16);
}

/** The value of an <input type="datetime-local"> (UTC) → ISO, or null when empty or invalid. */
export function fromUtcInput(value: string): string | null {
  if (!value) return null;
  const d = new Date(`${value.length === 16 ? `${value}:00` : value}Z`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** "2 min ago", "3 h ago", else the date. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86_400) return `${Math.floor(s / 86_400)} d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
